import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { useGetCurrentUserQuery } from "@nerdjs/account-kit";
import React, { PropsWithChildren, ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { RouterConfig } from "./hooks/config/routerConfig";
import { useCurrentUser } from "./redux/user/userHook";
import { CalendarPage } from "./routes/calendar";
import { ContractorPage } from "./routes/contractor";
import Contractors from "./routes/contractors";
import EmailPage from "./routes/email";
import Emails from "./routes/emails";
import GigPieces from "./routes/gigPieces";
import ICSSUbscription from "./routes/icsSubscription";
import { Insights } from "./routes/insights";
import { Journal } from "./routes/journal";
import NotFoundRoute from "./routes/notFoundRoute";
import { Profile } from "./routes/profile";
import { QrCode } from "./routes/qrCode";
import { ProjectPage } from "./routes/project";
import Projects from "./routes/projects";
import Seasons from "./routes/seasons";
import Services from "./routes/services";
import { Settings } from "./routes/settings";
import { VenuePage } from "./routes/venue";
import Venues from "./routes/venues";

const BoxWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hideMarginTop",
})<{ hideMarginTop?: boolean }>(({ theme, hideMarginTop }) => ({
  marginTop: 96,
  background: theme.palette.mode !== "light" ? "rgba(0,0,0,0.9)" : "#F5F5F7",
  backdropFilter: "blur(10px)",
  flexGrow: 1,
  display: "flex",
}));

/**
 *
 * @returns {React.ReactElement} the router
 */
export function AppRouter(): React.ReactElement {
  const { me: currentUser } = useCurrentUser();
  return (
    <RequireAuth>
      <Routes>
        <Route
          path={RouterConfig.root}
          element={<Navigate to={RouterConfig.journal} />}
        ></Route>
        <Route
          path={RouterConfig.journal}
          element={
            <BoxWrapper>
              <Journal />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.insights}
          element={
            <BoxWrapper>
              <Insights />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.calendar}
          element={
            <BoxWrapper>
              <CalendarPage />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.icsSubscription}
          element={
            <BoxWrapper>
              <ICSSUbscription />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.profile}
          element={
            <BoxWrapper>
              <Profile />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.qrCode}
          element={
            <BoxWrapper>
              <QrCode />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.gigPieces}
          element={
            <BoxWrapper>
              <GigPieces />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.services}
          element={
            <BoxWrapper>
              <Services />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.projects}
          element={
            <BoxWrapper>
              <Projects />
            </BoxWrapper>
          }
        >
          <Route path={":projectID/*"} element={<ProjectPage />} />
        </Route>
        <Route
          path={RouterConfig.seasons}
          element={
            <BoxWrapper>
              <Seasons />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={RouterConfig.venues}
          element={
            <BoxWrapper>
              <Venues />
            </BoxWrapper>
          }
        >
          <Route path={":venueID/*"} element={<VenuePage />} />
        </Route>
        <Route
          path={RouterConfig.contractors}
          element={
            <BoxWrapper>
              <Contractors />
            </BoxWrapper>
          }
        >
          <Route path={":contractorID/*"} element={<ContractorPage />} />
        </Route>
        <Route
          path={RouterConfig.emails}
          element={
            <BoxWrapper>
              <Emails />
            </BoxWrapper>
          }
        >
          <Route path={":emailID/*"} element={<EmailPage />} />
        </Route>
        <Route
          path={RouterConfig.settings}
          element={
            <BoxWrapper>
              <Settings />
            </BoxWrapper>
          }
        ></Route>
        <Route
          path={"*"}
          element={
            <BoxWrapper hideMarginTop={currentUser ? false : true}>
              <NotFoundRoute />
            </BoxWrapper>
          }
        />
      </Routes>
    </RequireAuth>
  );
}

/**
 * Route wrapper that redirects logged off users to the signin route
 *
 * @param {PropsWithChildren} props props
 * @returns {ReactElement | null} Return RequireAuth component
 */
function RequireAuth(props: PropsWithChildren): ReactElement | null {
  const { me: currentUser } = useCurrentUser();

  if (!currentUser) {
    // window.location.replace(AppConfig.front["401Redirect"]);
    return null;
  } else {
    return <>{props.children}</>;
  }
}
